/**
 * Terms of Service — plain English. Standard SaaS app terms adapted to Aura's reality.
 * Get a lawyer review before international scale; this is solid for U.S. launch.
 */
export function TermsOfServicePage({ onClose }) {
  return (
    <div className="legal-page" data-testid="terms-page">
      <div className="legal-container">
        <button className="legal-back-btn" onClick={onClose} data-testid="terms-back-btn">← Back</button>
        <h1>Terms of Service</h1>
        <p className="legal-meta">Last updated: February 2026</p>

        <p className="legal-intro">
          By using Aura, you agree to these terms. They're written in plain English so you can actually understand what you're agreeing to.
        </p>

        <h2>1. What Aura is</h2>
        <p>
          Aura is an AI companion app. It generates wallpapers and converses with you using large language models. Aura is <strong>not a real person</strong>, not a therapist, not a doctor, and not a substitute for professional help if you're in crisis. If you're in crisis, please contact 988 (U.S. Suicide & Crisis Lifeline) or your local emergency services.
        </p>

        <h2>2. Your account</h2>
        <ul>
          <li>You must be 13 or older to use Aura.</li>
          <li>Keep your password secret. You're responsible for activity on your account.</li>
          <li>One account per person. Don't share accounts.</li>
          <li>Be honest. Don't impersonate someone else when signing up.</li>
        </ul>

        <h2>3. The 7-day free trial</h2>
        <p>
          New accounts get 7 days of full access. After that, you need a paid plan to continue. No card is required for the trial — you only pay when you choose to.
        </p>

        <h2>4. Paid plans</h2>
        <ul>
          <li><strong>Base ($4.99 one-time):</strong> lifetime access to Aura's core features. No recurring charge.</li>
          <li><strong>Premium ($1.99/month):</strong> monthly subscription. Charges renew automatically. Cancel anytime in Settings → Manage subscription. You keep access until the end of the paid period.</li>
          <li><strong>Refunds:</strong> we'll honor refunds within 30 days of any charge. See our full <a href="#refund">Refund Policy</a> or just email <a href="mailto:support@wwwwhynot.com">support@wwwwhynot.com</a>.</li>
          <li><strong>Promo codes / referrals:</strong> codes can give discounts or free time. Codes are personal, non-transferable, and can be revoked if abused (e.g. mass-redemption schemes).</li>
        </ul>

        <h2>5. Your content</h2>
        <p>
          You own everything you send to Aura (your messages, photos, voice clips). By using Aura, you give us a limited license to process and store that content for the purpose of operating Aura — nothing else. We don't claim ownership and we don't sell it.
        </p>
        <p>
          Wallpapers Aura generates for you are yours to use personally — download them, set them as your phone background, share them with friends. Don't resell them as your own original art.
        </p>

        <h2>6. What's not allowed</h2>
        <ul>
          <li>Don't use Aura to do anything illegal.</li>
          <li>Don't try to trick Aura into generating sexual content involving minors, instructions for violence, malware, or other harmful output. We monitor for this.</li>
          <li>Don't try to break, scrape, or reverse-engineer Aura.</li>
          <li>Don't abuse referral codes or promo codes (creating fake accounts to farm rewards, etc).</li>
          <li>We can suspend or close accounts that violate these rules, without refund if abuse is involved.</li>
        </ul>

        <h2>7. AI limitations</h2>
        <p>
          Aura uses AI models that can sometimes produce incorrect, weird, or unintended responses. Don't rely on Aura for medical, legal, financial, or safety-critical decisions. Aura's "memories" of you are reconstructed from past chats and aren't always perfectly accurate.
        </p>

        <h2>8. Our liability</h2>
        <p>
          Aura is provided "as is." We work hard to make it reliable, but we can't guarantee zero downtime or zero bugs. Our maximum liability for any claim is limited to the amount you paid us in the 12 months before the claim arose.
        </p>

        <h2>9. Service changes & ending</h2>
        <ul>
          <li>We can add or remove features over time. We'll communicate major changes via in-app notification or email.</li>
          <li>You can close your account anytime (Settings → Delete my account).</li>
          <li>If we shut Aura down entirely, we'll give 30 days' notice and pro-rate any active premium subscriptions.</li>
        </ul>

        <h2>10. Disputes</h2>
        <p>
          These terms are governed by the laws of the United States and the State of [TBD — your state]. Any dispute will be resolved in U.S. courts. If you're a EU resident, your local consumer protection laws still apply where they're more protective.
        </p>

        <h2>11. Changes to terms</h2>
        <p>
          When we change these terms materially, we'll email you at least 14 days before the change takes effect. Continued use of Aura after that means you accept the new terms.
        </p>

        <h2>12. Contact</h2>
        <p>
          Questions or concerns? <a href="mailto:support@wwwwhynot.com">support@wwwwhynot.com</a>.
        </p>
      </div>
    </div>
  );
}
